//React
import { useState, useEffect, Fragment, useRef } from "react";

//Chakra
import {
  SimpleGrid,
  VStack,
  Box,
  Text,
  Flex,
  Button,
  Center,
  Input,
  FormControl,
  Textarea,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDescription,
  useDisclosure,
} from "@chakra-ui/react";

//Custom Component
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody.js";

//Endpoint
import { useFormik } from "formik";
import * as Yup from "yup";
import CardBadge from "components/Card/CardBadge";
import { getAllRoles } from "service/role.service";
import { createRole } from "service/role.service";
import { useAppToast } from "utils/toastUtil";

export default function Role() {
  // variable

  const [roles, setRoles] = useState([]);
  const { showToast } = useAppToast();
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  //form
  const formik = useFormik({
    initialValues: {
      name: "",
    },

    validationSchema: Yup.object().shape({
      name: Yup.string().required("required"),
    }),

    onSubmit: async (values) => {
      try {
        const res = await createRole(values);
        formik.resetForm();
        onClose();

        if (res) getRoles();
        showToast({
          title: "Success!",
          description: "Role created",
          status: "success",
        });
      } catch (error) {
        showToast({
          title: "Failed!",
          description: error,
          status: "error",
        });
      }
    },
  });

  //methods

  const getRoles = async () => {
    try {
      const roleLists = await getAllRoles();
      // console.log(roleLists.data);
      setRoles(roleLists.data);
    } catch (error) {
      console.log(error);
    }
  };

  //setup
  useEffect(() => {
    getRoles();
  }, []);

  //component
  // console.log(roles);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card  my={"4"}>
        <CardHeader p="6px 0px 22px 0px">
          <Text
            fontSize="lg"
            color="#fff"
            fontWeight="bold"
            textColor={"green.200"}
            textTransform={"uppercase"}
          >
            Role Info
          </Text>
        </CardHeader>
        <CardBody w={"full"}>
          <SimpleGrid
            w={"full"}
            my={"4"}
            px={"4"}
            columns={{ base: 1, md: 2 }}
            spacing={{ base: "12", md: "12" }}
          >
            <SimpleGrid
              my={"4"}
              px={"4"}
              columns={{ base: 1, md: 2 }}
              spacing={{ base: "4", md: "4" }}
            >
              <Text color={"white"}>ID</Text>
              <Text color={"white"}>Role</Text>
              {roles.map((role) => {
                return (
                  <Fragment key={role.id}>
                    <CardBadge props={role.id} />
                    <CardBadge props={role.name} />
                  </Fragment>
                );
              })}
            </SimpleGrid>

            <FormControl isInvalid={!!(formik?.errors.name && formik?.touched.name)}>
              <VStack alignItems={"start"} my={"3"}>
                <Text color={"green.200"}>Create Role</Text>
                <Text color={"white"}>Name</Text>
                <Input {...formik.getFieldProps("name")} bg={"white"} />
                <FormErrorMessage bottom={"-6"} position={"absolute"} textColor={"red"}>
                  {formik?.errors.name}
                </FormErrorMessage>
                <Center>
                  <Button
                    variant="brand"
                    fontSize="15px"
                    type="submit"
                    w="100%"
                    maxW="350px"
                    h="45"
                    mb="20px"
                    mt="20px"
                    mx={"2"}
                    textTransform={"uppercase"}
                    fontWeight={"extrabold"}
                    backgroundColor={"green.500"}
                    isLoading={formik.isSubmitting}
                    onClick={() => {
                      onOpen();
                    }}
                  >
                    + Role
                  </Button>
                </Center>
              </VStack>
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      <>
        <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Create Role
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure? You can't undo this action afterwards.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="green" onClick={formik.submitForm} ml={3}>
                  Create
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </>
    </Flex>
  );
}
